import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const CvnReportDetail: React.FC<{
  heatNumber: string;
  pipeLengthM?: number;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, pipeLengthM = 12.18, projectLimitFields = [], frequency, measurements = [], deletedMeasurementIds = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const [activeSet, setActiveSet] = useState('CVN-WELD');
  const cvnMeasurements = measurements.filter((measurement) => measurement.reportType === 'CVN');
  const projectLimitRows: Array<[string, React.ReactNode, boolean?]> = projectLimitFields.length
    ? projectLimitFields.map((field) => [field.label, field.limit, true])
    : [
      ['Test temperature', `${CVN_DETAIL.testTempC} C`, true],
      ['Average energy min', `>= ${CVN_DETAIL.requirements.avgEnergyJ} J`, true],
      ['Single energy min', `>= ${CVN_DETAIL.requirements.singleEnergyJ} J`, true],
      ['Shear area min', `>= ${CVN_DETAIL.requirements.shearPct} %`, true],
    ];
  const getCvnLimitText = (fieldPath: string, fallback: string) =>
    projectLimitFields.find((field) => field.fieldPath === fieldPath)?.limit || fallback;
  const avgLimitText = getCvnLimitText('avg_energy_j', `>= ${CVN_DETAIL.requirements.avgEnergyJ} J`);
  const singleLimitText = getCvnLimitText('single_energy_j', `>= ${CVN_DETAIL.requirements.singleEnergyJ} J`);
  const shearLimitText = getCvnLimitText('avg_shear_pct', `>= ${CVN_DETAIL.requirements.shearPct} %`);
  const cvnMeasurementByLocation = new Map<string, ReportMeasurementItem[]>();
  cvnMeasurements.forEach((measurement) => {
    const location = measurement.cvnLocation || 'pipe_body';
    cvnMeasurementByLocation.set(location, [...(cvnMeasurementByLocation.get(location) || []), measurement]);
  });
  const locationSets = CVN_DETAIL.sets.map((group) => ({
    id: group.id,
    location: group.location,
    specimens: (cvnMeasurementByLocation.get(group.location) || []).map((measurement) => ({
      id: measurement.cvnSpecimenId || measurement.id,
      energy: Number(measurement.cvnEnergyJ || measurement.measuredValue) || 0,
      shear: Number(measurement.cvnShearPct) || 0,
      lateralExpansion: Number(measurement.cvnLateralExpansionMm) || 0,
      status: measurement.status,
      size: measurement.cvnSpecimenSize || 'full',
      orientation: measurement.cvnNotchOrientation || 'transverse',
      measurement,
    })),
  }));
  const hasSpecimenRows = cvnMeasurements.length > 0;
  const set = locationSets.find((item) => item.id === activeSet) || locationSets[0];
  const avgEnergy = set.specimens.length ? average(set.specimens.map((specimen) => specimen.energy)) : 0;
  const minEnergy = set.specimens.length ? Math.min(...set.specimens.map((specimen) => specimen.energy)) : 0;
  const setStatus = set.specimens.some((specimen) => specimen.status === 'FAIL')
    ? 'FAIL'
    : set.specimens.some((specimen) => specimen.status === 'PENDING_REVIEW')
      ? 'PENDING_REVIEW'
      : avgEnergy >= CVN_DETAIL.requirements.avgEnergyJ && minEnergy >= CVN_DETAIL.requirements.singleEnergyJ ? 'PASS' : 'FAIL';
  const locationRows = locationSets
    .filter((group) => group.specimens.length > 0)
    .map((group) => {
    const energies = group.specimens.map((specimen) => specimen.energy);
    const avg = average(energies);
    const min = Math.min(...energies);
    const status = group.specimens.some((specimen) => specimen.status === 'FAIL')
      ? 'FAIL'
      : group.specimens.some((specimen) => specimen.status === 'PENDING_REVIEW')
        ? 'PENDING_REVIEW'
        : avg >= CVN_DETAIL.requirements.avgEnergyJ && min >= CVN_DETAIL.requirements.singleEnergyJ ? 'PASS' : 'FAIL';
    return {
      ...group,
      avg,
      min,
      status,
    };
  });
  const sizeRules = [
    { size: 'full', width: 10.0, factor: 1.00 },
    { size: '3/4', width: 7.5, factor: 0.75 },
    { size: '2/3', width: 6.7, factor: 0.67 },
    { size: '1/2', width: 5.0, factor: 0.50 },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">{formatLabel(set.location)} location · transverse notch</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">
              {set.specimens.length} specimens - full size · required average {avgLimitText}
            </div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('CVN')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Specimen
          </Button>
        </div>

        <div className="flex flex-wrap gap-2">
          {CVN_DETAIL.sets.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveSet(item.id)}
              className={cn(
                'rounded-lg border px-3 py-2 text-[12px] font-semibold',
                activeSet === item.id ? 'border-hero-red-20 bg-hero-red-10 text-readable-red' : 'border-[#E2E5EA] bg-white text-blueprint-blue'
              )}
            >
              {formatLabel(item.location)}
            </button>
          ))}
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-5">
            {hasSpecimenRows ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_190px]">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {set.specimens.map((specimen) => {
                  const maxEnergy = 250;
                  const pct = Math.min(100, (specimen.energy / maxEnergy) * 100);
                  const minPct = (CVN_DETAIL.requirements.singleEnergyJ / maxEnergy) * 100;
                  const avgPct = (CVN_DETAIL.requirements.avgEnergyJ / maxEnergy) * 100;
                  return (
                    <button
                      key={specimen.id}
                      type="button"
                      onClick={() => specimen.measurement && onEditMeasurement?.(specimen.measurement)}
                      className={cn(
                        'text-left',
                        specimen.measurement ? 'cursor-pointer' : 'cursor-default'
                      )}
                    >
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <span className="font-mono text-[12px] font-semibold text-blueprint-blue">{specimen.id}</span>
                        <span className="font-mono text-[12px] font-bold text-blueprint-blue">{specimen.energy} J</span>
                      </div>
                      <div className="relative h-32 overflow-hidden rounded border border-[#E2E5EA] bg-[#EEF0F3]">
                        <div className="absolute left-0 right-0 z-10 h-px bg-readable-red/70" style={{ bottom: `${minPct}%` }} />
                        <div className="absolute left-1 z-10 font-mono text-[9px] text-readable-red" style={{ bottom: `calc(${minPct}% + 3px)` }}>min indiv. {CVN_DETAIL.requirements.singleEnergyJ}</div>
                        <div className="absolute left-0 right-0 z-10 h-px bg-[#B38600]/70" style={{ bottom: `${avgPct}%` }} />
                        <div className="absolute left-1 z-10 font-mono text-[9px] text-[#8A6500]" style={{ bottom: `calc(${avgPct}% + 3px)` }}>req. avg {CVN_DETAIL.requirements.avgEnergyJ}</div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-galvanizing-green to-[#82C99A]" style={{ height: `${pct}%` }} />
                      </div>
                      <div className="mt-1 text-center font-mono text-[10px] text-heritage-grey">
                        shear {specimen.shear} % {specimen.measurement ? '· edit' : ''}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="border-t border-[#E2E5EA] pt-4 xl:border-l xl:border-t-0 xl:pl-4 xl:pt-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Average</div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="font-heading text-[30px] font-semibold text-blueprint-blue">{avgEnergy.toFixed(1)}</span>
                  <span className="text-[12px] text-text-grey">J</span>
                </div>
                <div className="text-[11px] text-heritage-grey">required {avgLimitText}</div>
                <div className="my-3 h-px bg-[#E2E5EA]" />
                <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Lowest individual</div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="font-heading text-[24px] font-semibold text-blueprint-blue">{minEnergy}</span>
                  <span className="text-[12px] text-text-grey">J</span>
                </div>
                <div className="text-[11px] text-heritage-grey">required {singleLimitText}</div>
                <div className="mt-3"><StatusBadge status={setStatus} /></div>
              </div>
            </div>
            ) : (
              <div className="rounded-lg border border-dashed border-[#E2E5EA] px-4 py-8 text-center text-[13px] text-heritage-grey">
                No specimens added yet. Click Add Specimen to enter CVN impact values.
              </div>
            )}
          </CardContent>
        </Card>

        {hasSpecimenRows && (
        <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="All test locations" subtitle="Each location (body / weld / HAZ) is a separate frequency check" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Specimens</th>
                  <th className="px-4 py-3 text-right">Avg energy</th>
                  <th className="px-4 py-3 text-right">Min individual</th>
                  <th className="px-4 py-3 text-right">Required avg</th>
                  <th className="px-4 py-3 text-right">Required min</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {locationRows.map((group) => (
                  <tr key={group.id} onClick={() => setActiveSet(group.id)} className={cn('cursor-pointer', group.id === activeSet && 'bg-page-bg')}>
                    <td className="px-4 py-3">{formatLabel(group.location)}</td>
                    <td className="px-4 py-3 font-mono">{group.specimens.length} x full</td>
                    <td className="px-4 py-3 text-right font-mono">{group.avg.toFixed(1)} J</td>
                    <td className="px-4 py-3 text-right font-mono">{group.min} J</td>
                    <td className="px-4 py-3 text-right font-mono">{avgLimitText}</td>
                    <td className="px-4 py-3 text-right font-mono">{singleLimitText}</td>
                    <td className="px-4 py-3"><StatusBadge status={group.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
        )}

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Specimen rows" subtitle="Use pencil/delete actions for  user-added CVN rows" />
            {hasSpecimenRows ? (
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Specimen</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Orient.</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3 text-right">Energy J</th>
                  <th className="px-4 py-3 text-right">Shear %</th>
                  <th className="px-4 py-3 text-right">Lat. exp.</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {locationSets.flatMap((group) => group.specimens.map((specimen) => (
                  <tr
                    key={`${group.id}-${specimen.id}`}
                    onClick={() => {
                      setActiveSet(group.id);
                      if (specimen.measurement) onEditMeasurement?.(specimen.measurement);
                    }}
                    className={cn('cursor-pointer', group.id === activeSet && 'bg-[#E7F3FF]')}
                  >
                    <td className="px-4 py-3 font-mono font-semibold">{specimen.id}</td>
                    <td className="px-4 py-3">{formatLabel(group.location)}</td>
                    <td className="px-4 py-3">{specimen.orientation}</td>
                    <td className="px-4 py-3">{specimen.size}</td>
                    <td className="px-4 py-3 text-right font-mono">{specimen.energy}</td>
                    <td className="px-4 py-3 text-right font-mono">{specimen.shear}</td>
                    <td className="px-4 py-3 text-right font-mono">{specimen.lateralExpansion.toFixed(2)} mm</td>
                    <td className="px-4 py-3"><StatusBadge status={specimen.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {specimen.measurement && (
                        <ItemActionButtons measurement={specimen.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      )}
                    </td>
                  </tr>
                )))}
              </tbody>
            </table>
            ) : (
              <div className="px-4 py-8 text-center text-[13px] text-heritage-grey">
                No specimen rows saved yet.
              </div>
            )}
          </CardContent>
        </Card>

        {hasSpecimenRows && (
        <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Sub-size correction" subtitle="Reduces the required average for specimens smaller than 10x10 mm" />
            <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4">
              {sizeRules.map((rule) => (
                <div
                  key={rule.size}
                  className={cn(
                    'rounded-lg border p-3',
                    rule.size === 'full' ? 'border-[#BBD9F4] bg-[#E7F3FF]' : 'border-[#E2E5EA] bg-white'
                  )}
                >
                  <div className="font-mono text-[10px] text-heritage-grey">{rule.size}</div>
                  <div className="mt-1 font-heading text-[16px] font-semibold text-blueprint-blue">{rule.width.toFixed(1)} mm</div>
                  <div className="text-[11px] text-heritage-grey">factor {rule.factor.toFixed(2)}</div>
                  <div className="mt-1 font-mono text-[11px] text-text-grey">adj. avg {(CVN_DETAIL.requirements.avgEnergyJ * rule.factor).toFixed(0)} J</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        )}

        {hasSpecimenRows && (
        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Specimen Locations" />
            <PipeLocationDiagram
              lengthM={pipeLengthM}
              marks={[
                { id: 'BODY', label: 'Body CVN', position: 0.20, status: 'PASS' },
                { id: 'WELD', label: 'Weld CVN', position: 0.50, status: 'PASS' },
                { id: 'HAZ', label: 'HAZ CVN', position: 0.50, status: 'PASS', below: true },
              ]}
            />
          </CardContent>
        </Card>
        )}
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', CVN_DETAIL.reportId, true],
          ['Performed at', formatDisplayDate(CVN_DETAIL.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', CVN_DETAIL.lab],
          ['Technician', CVN_DETAIL.technician],
          ['Specification', CVN_DETAIL.spec],
          ['Source file', CVN_DETAIL.sourceFile, true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows
              rows={[
                ['Frequency', frequency || 'per_heat', true],
                ['Locations', hasSpecimenRows ? `${locationRows.length} of 3` : '0 of 3', true],
                ['Specimens', hasSpecimenRows ? `${cvnMeasurements.length} total` : '0 total', true],
              ]}
            />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
       
    </div>
  );
};
